import React, { useState, useEffect } from 'react';
import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, IconButton, Tooltip } from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
// Lantern logo for the button - using public folder

const LeftDrawer = ({
  isAuthenticated,
  setIsAuthenticated,
  onFileUpload,
  uploadedFiles,
  dataIndexToSaveHandler,
  activeSection,
  setActiveSection,
  buildHandoffUrl,
  onOpenMLStudio,
  onMissingLanternBase,
}) => {
  const navigate = useNavigate();
  const resolveLanternBase = () => {
    const rawBase = process.env.REACT_APP_PUBLIC_LANTERN_V3_BASE || '';
    if (rawBase) {
      return rawBase.replace(/\/+$/, '');
    }
    if (process.env.NODE_ENV === 'production') {
      return null;
    }
    return 'http://localhost:3000';
  };

  const handleMissingLanternBase = () => {
    if (onMissingLanternBase) {
      onMissingLanternBase();
      return;
    }
    window.alert('Lantern base URL is not configured.');
  };

  const handleSidebarItem = (label, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check if item is disabled
    const disabledItems = ['ML Studio', 'Team Panel', 'Billing & Usage', 'Help Centre', 'Settings'];
    if (disabledItems.includes(label)) {
      // Disabled items - do nothing
      return;
    }
    
    setActiveSection(label);
    
    // Handle navigation for specific items
    switch (label) {
      case 'Home':
        navigate('/portal');
        break;
      case 'My Cloud Space':
        // Navigate to Lantern Cloud Space page with SSO fragment
        const lanternBase = resolveLanternBase();
        if (!lanternBase) {
          handleMissingLanternBase();
          break;
        }
        
        const cloudHandoffUrl = buildHandoffUrl ? buildHandoffUrl(lanternBase, '/datahub') : null;
        const cloudTargetUrl = cloudHandoffUrl || `${lanternBase}/datahub`;
        if (cloudHandoffUrl) {
          window.location.replace(cloudTargetUrl);
          break;
        }
        window.location.assign(cloudTargetUrl);
        break;
      case 'Lantern':
        // Navigate to Lantern app - using external URL for now
        const lanternAppBase = resolveLanternBase();
        if (!lanternAppBase) {
          handleMissingLanternBase();
          break;
        }
        const handoffUrl = buildHandoffUrl ? buildHandoffUrl(lanternAppBase, '/') : null;
        const lanternTargetUrl = handoffUrl || `${lanternAppBase}/`;
        console.log('[Auth-Portal] Lantern navigation target:', lanternTargetUrl);
        if (handoffUrl) {
          window.location.replace(lanternTargetUrl);
          break;
        }
        window.location.assign(lanternTargetUrl);
        break;
      case 'ML Studio':
        navigate('/ml-studio');
        break;
      case 'Billing & Usage':
        navigate('/billing');
        break;
      case 'Team Panel':
        navigate('/team-panel');
        break;
      case 'Settings':
        navigate('/settings');
        break;
      case 'Help Centre':
        navigate('/help-center');
        break;
      default:
        // For other items, just update the active section
        break;
    }
  };

  // SIDEBAR LIST =====================
  // ==================================
  const CoreModules = [
    { icon: <DashboardOutlinedIcon sx={{ fontSize: 22 }} />, label: 'Home' },
    { icon: <CloudOutlinedIcon sx={{ fontSize: 22 }} />, label: 'My Cloud Space' },
    { icon: <VisibilityOutlinedIcon sx={{ fontSize: 22 }} />, label: 'Lantern' },
    { icon: <PsychologyOutlinedIcon sx={{ fontSize: 22 }} />, label: 'ML Studio', disabled: true },
  ];

  const CollaborationAccount = [
    { icon: <GroupOutlinedIcon sx={{ fontSize: 22 }} />, label: 'Team Panel', disabled: true },
    { icon: <CreditCardOutlinedIcon sx={{ fontSize: 22 }} />, label: 'Billing & Usage', disabled: true },
    { icon: <SettingsOutlinedIcon sx={{ fontSize: 22 }} />, label: 'Settings', disabled: true },
    { icon: <HelpOutlineIcon sx={{ fontSize: 22 }} />, label: 'Help Centre', disabled: true },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          backgroundColor: 'var(--color-surface-50)',
          borderRight: '1px solid var(--color-border-200)',
          overflowX: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '1rem',
          borderBottom: '1px solid var(--color-border-200)',
          minHeight: '64px',
        }}
      >
        <img 
          src="/logo.png" 
          alt="Datafre8" 
          style={{ 
            height: '40px', 
            width: 'auto',
            cursor: 'pointer'
          }} 
        />
      </Box>

      <List sx={{ padding: '0.5rem' }}>
        {/* Core Modules Section */}
        <Typography
          variant="caption"
          sx={{
            color: 'var(--color-text-500)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            padding: '0.5rem 1rem 0.25rem 1rem',
            fontSize: '0.75rem',
          }}
        >
          Core Modules
        </Typography>
        {CoreModules.map((item, index) => (
          <ListItemButton
            key={item.label}
            onClick={(e) => handleSidebarItem(item.label, e)}
            disabled={item.disabled}
            sx={{
              marginBottom: '0.25rem',
              borderRadius: '8px',
              backgroundColor: activeSection === item.label ? 'var(--color-primary-50)' : 'transparent',
              color: item.disabled ? 'var(--color-text-500)' : (activeSection === item.label ? 'var(--color-primary-600)' : 'var(--color-text-900)'),
              opacity: item.disabled ? 0.5 : 1,
              cursor: item.disabled ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: item.disabled ? 'transparent' : (activeSection === item.label ? 'var(--color-primary-50)' : 'var(--color-neutral-300)'),
              },
              '& .MuiListItemIcon-root': {
                color: item.disabled ? 'var(--color-text-500)' : (activeSection === item.label ? 'var(--color-primary-600)' : 'var(--color-text-500)'),
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <span>{item.label}</span>
                  {item.disabled && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'var(--color-text-500)', fontStyle: 'italic', mt: 0.25 }}>
                      Coming soon
                    </Typography>
                  )}
                </Box>
              }
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: activeSection === item.label ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}

        {/* Divider */}
        <Divider sx={{ margin: '1rem 0.5rem' }} />

        {/* Collaboration & Account Section */}
        <Typography
          variant="caption"
          sx={{
            color: 'var(--color-text-500)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            padding: '0.5rem 1rem 0.25rem 1rem',
            fontSize: '0.75rem',
          }}
        >
          Collaboration & Account
        </Typography>
        {CollaborationAccount.map((item, index) => (
          <ListItemButton
            key={item.label}
            onClick={(e) => handleSidebarItem(item.label, e)}
            disabled={item.disabled}
            sx={{
              marginBottom: '0.25rem',
              borderRadius: '8px',
              backgroundColor: activeSection === item.label ? 'var(--color-primary-50)' : 'transparent',
              color: item.disabled ? 'var(--color-text-500)' : (activeSection === item.label ? 'var(--color-primary-600)' : 'var(--color-text-900)'),
              opacity: item.disabled ? 0.5 : 1,
              cursor: item.disabled ? 'default' : 'pointer',
              '&:hover': {
                backgroundColor: item.disabled ? 'transparent' : (activeSection === item.label ? 'var(--color-primary-50)' : 'var(--color-neutral-300)'),
              },
              '& .MuiListItemIcon-root': {
                color: item.disabled ? 'var(--color-text-500)' : (activeSection === item.label ? 'var(--color-primary-600)' : 'var(--color-text-500)'),
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <span>{item.label}</span>
                  {item.disabled && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'var(--color-text-500)', fontStyle: 'italic', mt: 0.25 }}>
                      Coming soon
                    </Typography>
                  )}
                </Box>
              }
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: activeSection === item.label ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}
      </List>


    </Drawer>
  );
};

export default LeftDrawer; 
